"use strict";
// Every colour on the themed voice screen, derived from the widget's primary
// colour. Light mode port of `vwTheme` from the "Voice Widget — State System"
// design canvas.
exports.__esModule = true;
exports.voiceTheme = exports.contrastRatio = exports.parseBrandColor = exports.DEFAULT_BRAND_COLOR = void 0;
exports.DEFAULT_BRAND_COLOR = '#0E4498'; // the widget's default primary-color
var WHITE = [255, 255, 255];
var BLACK = [0, 0, 0];
var NEAR_BLACK = [11, 11, 12];
// Brand-coloured marks (bars, spinner, links) must stay this readable on white.
var MIN_INK_CONTRAST = 3.2;
exports.parseBrandColor = function (value) {
    var hex = (value || '').trim().replace(/^#/, '');
    if (/^[0-9a-f]{3}$/i.test(hex)) {
        hex = hex
            .split('')
            .map(function (c) { return c + c; })
            .join('');
    }
    if (!/^[0-9a-f]{6}$/i.test(hex))
        return exports.parseBrandColor(exports.DEFAULT_BRAND_COLOR);
    return [0, 2, 4].map(function (i) { return parseInt(hex.slice(i, i + 2), 16); });
};
var luminance = function (c) {
    var _a = c.map(function (v) {
        var s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow(((s + 0.055) / 1.055), 2.4);
    }), r = _a[0], g = _a[1], b = _a[2];
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
exports.contrastRatio = function (a, b) {
    var x = luminance(a);
    var y = luminance(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
};
var mix = function (c, to, k) {
    return c.map(function (v, i) { return Math.round(v + (to[i] - v) * k); });
};
var rgb = function (c) { return "rgb(" + c[0] + "," + c[1] + "," + c[2] + ")"; };
var rgba = function (c, a) { return "rgba(" + c[0] + "," + c[1] + "," + c[2] + "," + a + ")"; };
var rotateHue = function (c, deg) {
    var _a = c.map(function (v) { return v / 255; }), r = _a[0], g = _a[1], b = _a[2];
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var l = (max + min) / 2;
    if (max === min)
        return c; // grey: no hue to rotate
    var d = max - min;
    var s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    var h;
    if (max === r)
        h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g)
        h = (b - r) / d + 2;
    else
        h = (r - g) / d + 4;
    h = (h / 6 + deg / 360 + 1) % 1;
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p = 2 * l - q;
    var channel = function (t) {
        var x = t;
        if (x < 0)
            x += 1;
        if (x > 1)
            x -= 1;
        if (x < 1 / 6)
            return p + (q - p) * 6 * x;
        if (x < 1 / 2)
            return q;
        if (x < 2 / 3)
            return p + (q - p) * (2 / 3 - x) * 6;
        return p;
    };
    return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)].map(function (v) {
        return Math.round(v * 255);
    });
};
// Darken toward black until the colour is readable on white.
var readableInk = function (c) {
    var k = 0;
    var out = c;
    while (exports.contrastRatio(out, WHITE) < MIN_INK_CONTRAST && k < 1) {
        k += 0.04;
        out = mix(c, BLACK, k);
    }
    return out;
};
exports.voiceTheme = function (brandColor) {
    var c = exports.parseBrandColor(brandColor);
    var alt = rotateHue(c, 40);
    var grey = Math.round((c[0] + c[1] + c[2]) / 3);
    var toGrey = [grey, grey, grey];
    var auraMain = mix(c, toGrey, 0.35);
    var auraSide = mix(alt, toGrey, 0.45);
    var onBrand = exports.contrastRatio(WHITE, c) >= exports.contrastRatio(NEAR_BLACK, c)
        ? '#FFFFFF'
        : '#0B0B0C';
    return {
        brand: rgb(c),
        onBrand: onBrand,
        ink: rgb(readableInk(c)),
        tint05: rgba(c, 0.05),
        tint12: rgba(c, 0.12),
        tint24: rgba(c, 0.24),
        tint32: rgba(c, 0.32),
        orbBlobs: [
            rgba(mix(c, WHITE, 0.15), 0.95),
            rgba(mix(alt, WHITE, 0.2), 0.9),
            rgba(mix(rotateHue(c, -40), WHITE, 0.25), 0.9),
            rgba(mix(c, WHITE, 0.6), 0.95),
            rgba(mix(rotateHue(c, 160), WHITE, 0.45), 0.7),
        ],
        orbBase: rgb(mix(c, WHITE, 0.94)),
        orbShade: rgba(mix(c, BLACK, 0.3), 0.16),
        orbHalo: rgba(c, 0.2),
        orbHalo2: rgba(alt, 0.1),
        auraCore: rgba(auraMain, 0.17),
        auraEdge: rgba(auraMain, 0.06),
        auraAlt: rgba(auraSide, 0.11),
        auraB: rgba(auraMain, 0.14),
        auraC: rgba(auraMain, 0.1),
        surface: '#FFFFFF',
        raise: '#FFFFFF',
        line: 'rgba(16,18,24,.08)',
        text: '#0F1115',
        muted: '#5A606C',
        chip: '#F4F5F7',
        frost: 'rgba(255,255,255,.46)',
        glow: 'rgba(255,255,255,.35)',
        rim: 'inset 0 0 0 1px rgba(255,255,255,.7), inset 0 1px 0 rgba(255,255,255,.95), ' +
            'inset 0 0 22px rgba(255,255,255,.5), inset 0 -16px 30px rgba(255,255,255,.3)',
    };
};
